/**
 * Lifecycle status of a product (TO-415 design D3). The allowed moves are
 * exactly `DRAFT → ACTIVE`, `ACTIVE → RETIRED` and `RETIRED → ACTIVE`, applied
 * only through `POST /products/{id}/transitions`. Persisted as `varchar` + CHECK
 * (never a Postgres ENUM, ADR-0011). `PAUSED` was removed (D3).
 */
export enum ProductStatus {
	DRAFT = 'DRAFT',
	ACTIVE = 'ACTIVE',
	RETIRED = 'RETIRED',
}

/** How a product entered the catalog. Immutable; always `MANUAL` over HTTP. */
export enum ProductOrigin {
	MANUAL = 'MANUAL',
	IMPORT = 'IMPORT',
}

/** The kind of a structured product call to action; each kind has its own target rule. */
export enum ProductCtaType {
	EXTERNAL_LINK = 'EXTERNAL_LINK',
	PHONE = 'PHONE',
	SCHEDULE_MEETING = 'SCHEDULE_MEETING',
	IN_APP_ACTION = 'IN_APP_ACTION',
}

/**
 * Why the product domain importer refuses one import plan entry (TO-404
 * BR-7/BR-8/BR-12). Stable: they land in the file-exchange change log.
 */
export enum ProductImportErrorCode {
	PRODUCT_CODE_MANUAL = 'PRODUCT_CODE_MANUAL',
	PRODUCT_CODE_TAKEN = 'PRODUCT_CODE_TAKEN',
	PRODUCT_LOCALE_NOT_ENABLED = 'PRODUCT_LOCALE_NOT_ENABLED',
	PRODUCT_TRANSLATION_INVALID = 'PRODUCT_TRANSLATION_INVALID',
	PRODUCT_TRANSLATIONS_MISSING = 'PRODUCT_TRANSLATIONS_MISSING',
	PRODUCT_CATEGORY_INVALID = 'PRODUCT_CATEGORY_INVALID',
	PRODUCT_CTA_INVALID = 'PRODUCT_CTA_INVALID',
	PRODUCT_IMPORT_CONFLICT = 'PRODUCT_IMPORT_CONFLICT',
}

/** Where a translation's copy came from: the admin API writes `MANUAL`, the product import `IMPORT`. */
export enum ProductTranslationSource {
	MANUAL = 'MANUAL',
	AI_TRANSLATED = 'AI_TRANSLATED',
	/** Written by the product file import (TO-404). */
	IMPORT = 'IMPORT',
}

/**
 * A catalog product (TO-415 v2). A product belongs to exactly one tenant and is
 * identified within it by a unique `code`. Marketing copy lives in
 * per-locale translations ({@link IProductTranslation}), not on the product.
 */
export interface IProduct {
	/** Internal, app-owned id (primary key, uuid v7, globally unique). */
	productId: string
	/** The tenant this product belongs to. */
	tenantId: string
	/** Human/business code, unique WITHIN a tenant (e.g. `SAV-001`). */
	code: string
	/** Canonical display name (DB column `canonical_name`). */
	name: string
	/** Default locale tag for the product, e.g. `es`. */
	defaultLocale: string
	status: ProductStatus
	origin: ProductOrigin
	/** A same-tenant category, or null. */
	categoryId: string | null
	/** Opaque partner payload (≤ 16384 bytes serialized). */
	partnerAttributes: Record<string, unknown>
	featured: boolean
	icon: string | null
	/** Opaque file-service reference; the URL is never stored. */
	imageFileId: string | null
	ctaPrimaryType: ProductCtaType | null
	ctaPrimaryTarget: string | null
	ctaSecondaryType: ProductCtaType | null
	ctaSecondaryTarget: string | null
	createdAt: Date
	updatedAt: Date
}

/** A tenant-scoped product category (flat lookup). */
export interface IProductCategory {
	categoryId: string
	code: string
	name: string
	icon: string | null
	sortOrder: number
	createdAt: Date
	updatedAt: Date
}

/** One locale's marketing copy for a product. At most one live row per (product, locale). */
export interface IProductTranslation {
	locale: string
	name: string
	tagline: string | null
	shortDescription: string | null
	longDescription: string | null
	benefits: string[]
	mainCtaLabel: string | null
	secondaryCtaLabel: string | null
	source: ProductTranslationSource
	lastEditedBy: string | null
	lastEditedAt: Date
}

/** Which locales publishing requires, and which of them are still untranslated. */
export interface ProductTranslationCompleteness {
	requiredLocales: string[]
	missingLocales: string[]
	publishable: boolean
}
